import localStorageFallback from 'local-storage-fallback';
import {
  setupTracer,
  createTracedFetch,
  Tags,
  ShouldTraceCallback,
  BeforeRequestCallback,
  FetchHelpers,
  BrowserHelpers,
  SpotifyHelpers,
  AfterResponseCallback,
  ExtractOperationCallback,
} from '@spotify-internal/tracing-web';
import { getHash } from '@mrkt/features/env';
import { addOwnershipInfo } from './tracing-ownership';
import { getSessionId, SESSION_ID_TAG } from './session-id';
import { addWebsiteOriginPodTag } from './TraceUtils';

export function initializeTracing() {
  const isProduction = process.env.NODE_ENV === 'production';

  if (process.env.NODE_ENV === 'test') return;

  // It's safe to call this as many times, initialization internally only happens once.
  const tracer = setupTracer({
    dev: !isProduction,

    component_name: 'mrkt-web',
    nodejs_instrumentation: false,
    fetch_instrumentation: false,
    xhr_instrumentation: false,
    instrument_page_load: true,

    // false to prevent logging user tokens/sp_dc and accessible from the web UI!
    include_cookies: false,

    // 2: Log all errors to console
    // https://github.com/lightstep/lightstep-tracer-javascript#new-traceroptions
    verbosity: isProduction ? 0 : 2,

    default_span_tags: {
      // Ensure we get the session id with _everything_
      [SESSION_ID_TAG]: getSessionId(),
      // Set if puppeteer / webdriver: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/webdriver
      [Tags.NAVIGATOR_WEBDRIVER]: navigator.webdriver,
    },
  });

  window.fetch = createTracedFetch(
    { fetch: window.fetch },
    {
      tracer,
      shouldTrace,
      beforeRequest,
      afterResponse,
      extractOperation,
    },
  );
}

const enableCanary = !!localStorageFallback.getItem('CANARY_TRACE');
const NO_TRACE = String(Math.random());

export const shouldTrace: ShouldTraceCallback = req => {
  const tracedMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  const tracedURLHosts = [
    'generic.wg.spotify.com',
    'artist-identity-image.spotify.com',
    enableCanary ? 'artist-storylines-upload.spotify.com' : NO_TRACE,
    'api.spotify.com',
    `${window.location.host}/api/graphql`,
  ] as const;

  return (
    tracedMethods.some(m => m === req.method) &&
    tracedURLHosts.some(p => req.url.indexOf(p) > -1)
  );
};

const beforeRequest: BeforeRequestCallback = (span, req) => {
  FetchHelpers.addDefaultRequestTags(span, req);
  SpotifyHelpers.addWebgateServiceNameTag(span, req);
  SpotifyHelpers.addServiceInfoTags(
    span,
    // We don't increment the version in package.json, so it's better to use the
    // hash as the version.
    getHash(),
    getHash(),
  );
  BrowserHelpers.addDefaultNavigatorTags(span, navigator);
  addWebsiteOriginPodTag(span);
  addOwnershipInfo(span, req);
};

const afterResponse: AfterResponseCallback = (span, res) => {
  FetchHelpers.addDefaultResponseTags(span, res);
};

// Exported for test-only
export const extractOperation: ExtractOperationCallback = req => {
  const info = SpotifyHelpers.extractWebgateInfo(req);
  const hostname = FetchHelpers.extractHostname(req);
  if (info) return `fetch.${info.service}`;
  if (hostname) return `fetch.${hostname}`;
  return 'fetch';
};
